import React, {useContext} from 'react';
import ClustersMarketContext from '../contexts/ClustersMarketContext';

const UNSORTED_TOOLTIP =
    'Pages with no readable text (video hosts, app shells) — clustered but not shown as topics';

interface UrlCountBadgeProps {
    /**
     * Override the context-provided `totalUrlCount`. Production code leaves
     * this undefined and reads from `ClustersMarketContext`; callers without
     * a provider in scope (e.g. `UrlExplorerPage`'s filtered result count,
     * or tests) pass an explicit value instead.
     */
    count?: number | null;
    /** Override the context-provided `unsortedUrlCount`. See `count`. */
    unsortedCount?: number | null;
}

/**
 * Rolling 7-day URL-count pill (bd deepcontext-0ihe, extracted bd
 * deepcontext-r4xu).
 *
 * Reads `totalUrlCount`/`unsortedUrlCount` from `ClustersMarketContext` —
 * published by the overview page once its `/api/clusters` payload resolves.
 * Shared by `ClustersTopBar` (portalled into the page-header row for every
 * other Universe sub-page) and `ClustersPage` (rendered inline in its own
 * controls row, alongside the AnchorBar — the page-header slot never grows
 * past its content width, so a flex-growing search bar has to live in the
 * page body). `UrlExplorerPage` overrides `count` with its filtered result
 * total and pins `unsortedCount` to 0 — the market-wide unsorted count from
 * context would be mismatched (and often larger) next to a filtered/
 * paginated total, so it's always suppressed there rather than inherited
 * from context (bd deepcontext-5l2g.2).
 */
const UrlCountBadge: React.FC<UrlCountBadgeProps> = ({count, unsortedCount}) => {
    const ctx = useContext(ClustersMarketContext);
    const totalUrlCount = count !== undefined ? count : ctx?.totalUrlCount ?? null;
    const unsortedUrlCount = unsortedCount !== undefined ? unsortedCount : ctx?.unsortedUrlCount ?? null;
    return (
        <span
            data-testid="clusters-url-count-badge"
            className="inline-flex items-center h-[42px] text-xs font-semibold text-ink-muted bg-ghost-bg rounded-full px-3 whitespace-nowrap"
        >
            <span className="font-semibold text-ink">
                {totalUrlCount !== null ? totalUrlCount.toLocaleString('en-US') : '—'}
            </span>{' '}
            URLs
            <span title="Rolling 7-day window of classified URLs, refreshed daily">
                {' '}
                · last 7 days
            </span>
            {unsortedUrlCount !== null && unsortedUrlCount > 0 && (
                <span
                    data-testid="clusters-url-unsorted-segment"
                    className="text-ink-soft"
                    title={UNSORTED_TOOLTIP}
                >
                    {' '}
                    · {unsortedUrlCount.toLocaleString('en-US')} unsorted
                </span>
            )}
        </span>
    );
};

export default UrlCountBadge;
